import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { GoogleButton, Divider, AuthFields } from "./authParts";

const AuthModal = () => {
  const { authOpen, closeAuth, emailLogin, emailRegister } = useAuth();
  const [tab, setTab] = useState("signin");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });

  // Clear the form (incl. password from memory) and reset to the sign-in tab when closed.
  useEffect(() => {
    if (!authOpen) {
      setForm({ name: "", email: "", password: "" });
      setTab("signin");
    }
  }, [authOpen]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (tab === "signin") {
        await emailLogin(form.email, form.password);
        toast.success("Welcome back!");
      } else {
        await emailRegister(form.name, form.email, form.password);
        toast.success("Account created. Welcome!");
      }
      closeAuth();
      setForm({ name: "", email: "", password: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={authOpen} onOpenChange={(o) => (!o ? closeAuth() : null)}>
      <DialogContent className="sm:max-w-md bg-[#fdfdfb]" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#14140f]">
            {tab === "signin" ? "Welcome back" : "Create your account"}
          </DialogTitle>
          <DialogDescription className="text-[#14140f]/55">
            Track your learning, save notes and follow tracks across the wisdom graph.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="mt-1">
          <TabsList className="grid grid-cols-2 w-full bg-[#f5f4f0]">
            <TabsTrigger value="signin" data-testid="tab-signin">Sign in</TabsTrigger>
            <TabsTrigger value="register" data-testid="tab-register">Register</TabsTrigger>
          </TabsList>

          <form onSubmit={submit} className="mt-5 space-y-4">
            <AuthFields tab={tab} form={form} set={set} />
            <Button
              type="submit"
              disabled={loading}
              data-testid="auth-submit-btn"
              className="w-full rounded-full bg-[#ff7360] hover:bg-[#e85a46] text-[#14140f]"
            >
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {tab === "signin" ? "Sign in" : "Create account"}
            </Button>
          </form>
        </Tabs>

        <Divider />
        <GoogleButton />
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
